import { FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import { lazy, Suspense, type ReactNode, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoadingSkeleton } from "./components/LoadingSkeleton";
import { CurrencyProvider } from "./context/CurrencyContext";
import { useAuth } from "./hooks/useAuth";
import { LoginPage } from "./pages/LoginPage";
import "./styles/app.css";

const AboutPage = lazy(() => import("./pages/AboutPage").then((module) => ({ default: module.AboutPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const GovernancePage = lazy(() => import("./pages/GovernancePage").then((module) => ({ default: module.GovernancePage })));
const OrphanedResourcesPage = lazy(() =>
  import("./pages/OrphanedResourcesPage").then((module) => ({ default: module.OrphanedResourcesPage }))
);
const PerformanceOptimizationPage = lazy(() =>
  import("./pages/PerformanceOptimizationPage").then((module) => ({ default: module.PerformanceOptimizationPage }))
);
const PortfolioPage = lazy(() => import("./pages/PortfolioPage").then((module) => ({ default: module.PortfolioPage })));
const SavingsPage = lazy(() => import("./pages/SavingsPage").then((module) => ({ default: module.SavingsPage })));
const SubscriptionsPage = lazy(() => import("./pages/SubscriptionsPage").then((module) => ({ default: module.SubscriptionsPage })));
const TagAllocationPage = lazy(() => import("./pages/TagAllocationPage").then((module) => ({ default: module.TagAllocationPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

function LazyPage({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<LoadingSkeleton rows={6} />}>
      {children}
    </Suspense>
  );
}

function ProtectedShell({ themeMode, onThemeChange }: { themeMode: "dark" | "light"; onThemeChange: (value: "dark" | "light") => void }) {
  const { session } = useAuth();

  if (session.isLoading) {
    return (
      <main className="centered-state">
        <div className="state-panel">
          <LoadingSkeleton rows={5} />
        </div>
      </main>
    );
  }

  if (!session.data?.authenticated || !session.data.azureAccessReady) {
    return <Navigate to="/" replace />;
  }

  return <AppShell themeMode={themeMode} onThemeChange={onThemeChange} />;
}

function AppRoutes({ themeMode, onThemeChange }: { themeMode: "dark" | "light"; onThemeChange: (value: "dark" | "light") => void }) {
  return (
    <AnimatePresence mode="wait">
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route element={<ProtectedShell themeMode={themeMode} onThemeChange={onThemeChange} />}>
          <Route path="/dashboard" element={<LazyPage><DashboardPage /></LazyPage>} />
          <Route path="/portfolio" element={<LazyPage><PortfolioPage /></LazyPage>} />
          <Route path="/subscriptions" element={<LazyPage><SubscriptionsPage /></LazyPage>} />
          <Route path="/performance" element={<LazyPage><PerformanceOptimizationPage /></LazyPage>} />
          <Route path="/savings" element={<LazyPage><SavingsPage /></LazyPage>} />
          <Route path="/orphaned" element={<LazyPage><OrphanedResourcesPage /></LazyPage>} />
          <Route path="/governance" element={<LazyPage><GovernancePage /></LazyPage>} />
          <Route path="/tag-allocation" element={<LazyPage><TagAllocationPage /></LazyPage>} />
          <Route path="/insights" element={<Navigate to="/dashboard#cost-insights" replace />} />
          <Route path="/snapshots" element={<Navigate to="/subscriptions" replace />} />
          <Route path="/about" element={<LazyPage><AboutPage /></LazyPage>} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  const [themeMode, setThemeMode] = useState<"dark" | "light">("dark");
  const fluentTheme = useMemo(() => (themeMode === "dark" ? webDarkTheme : webLightTheme), [themeMode]);

  return (
    <ErrorBoundary>
      <FluentProvider theme={fluentTheme} className={themeMode === "dark" ? "theme-dark" : "theme-light"}>
        <QueryClientProvider client={queryClient}>
          <CurrencyProvider>
            <BrowserRouter>
              <AppRoutes themeMode={themeMode} onThemeChange={setThemeMode} />
            </BrowserRouter>
          </CurrencyProvider>
        </QueryClientProvider>
      </FluentProvider>
    </ErrorBoundary>
  );
}
